import { stringify } from 'qs';
import { useMemo } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Button, Heading } from 'yoso';
import { T } from '~/components/I18n';
import { Level } from '~/components/Level';
import { LevelMeta } from '~/components/Level/Level.hooks';
import { CitizenLevelStatusFigure } from '~/components/Level/Status/Figure/CitizenLevelStatusFigure';
import { useLevelStatuses } from '~/components/Level/Status/LevelStatus.hooks';
import { useCitizensByLevel } from '~/components/Membership/Membership.hooks';
import { NftCard } from '~/components/Nft/Card';
import { Progress } from '~/components/Progress';
import './LevelStatusCard.scss';

export type LevelStatusCardProps = LevelMeta;
export const LevelStatusCard = ({ level, title }: LevelStatusCardProps) => {
  const { data: citizens } = useCitizensByLevel(level, { useErrorBoundary: false });
  const { data: statuses } = useLevelStatuses();
  const status = useMemo(
    () => statuses.find(({ level: l }) => l === level.toString()),
    [level, statuses]
  );
  const hasNextLevel = !!status;
  const citizenCount = citizens.length;

  return (
    <NftCard className="level-status-card" direction="row" size="medium">
      <div className="card-header">
        <CitizenLevelStatusFigure level={level} className="nft-card-content" />
      </div>
      <div className="card-body">
        <Heading level={5} className="level-status-card-title">
          <small>
            <Level level={level} />
          </small>
          {title}
        </Heading>
        {hasNextLevel && (
          <Progress
            className="level-status-card-progress"
            max={100}
            value={status?.percentage}
            bubble={false}
          />
        )}
        {hasNextLevel && (
          <Button<LinkProps>
            as={Link}
            to={{
              pathname: '/membership/item/citizen/levelup/go',
              search: stringify({ level: level }),
            }}
            size="small"
            className="level-status-card-button"
            color={status?.canLevelUp ? 'primary' : 'default'}>
            <T
              name={
                status?.canLevelUp
                  ? 'citizen_button_title_allset'
                  : 'citizen_button_title_seedetails'
              }
              pre="nowrap"
              options={[level + 1]}
            />
          </Button>
        )}
      </div>
      <div className="card-footer">
        <span className="level-status-card-citizen-count">
          {status?.nftCount ?? (citizenCount || '')}
          {status && `/${status.minNftAmount}`}
        </span>
      </div>
    </NftCard>
  );
};
